import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, LoadingOverlay } from 'dashboard/components/common';
import WarehouseDialog from './WarehouseDialog';

/**
 * Stock locations tab. Renders a flat card list — there's rarely many
 * locations, so a heavy table is overkill.
 */
export default function WarehousesTab({ brandId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listShopWarehouses({ brand_id: brandId })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listShopWarehouses({ brand_id: brandId })
      .then((res) => { if (!cancelled) setRows(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load', { toastId: 'warehouses-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const remove = (row) => {
    if (!window.confirm(`Remove "${row.name}"?`)) return;
    API.deleteShopWarehouse({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Removed'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='14px' flexWrap='wrap'>
        <Box>
          <Typography fontWeight='700'>Warehouses & stock locations</Typography>
          <Typography variant='body2' color='text.secondary'>
            Where your inventory physically lives. The fulfillment planner picks the closest one with stock.
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setDlgOpen(true); }}
        >
          New location
        </Button>
      </Stack>
      <LoadingOverlay loading={loading && rows.length === 0} minHeight={180}>
        {!loading && rows.length === 0 && <EmptyState>No locations yet. Add your first warehouse or store.</EmptyState>}
        {rows.map((w) => (
        <Stack
          key={w._id}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          gap='10px'
          sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: '12px',
            p: '14px', mb: '10px',
          }}
        >
          <Box>
            <Stack direction='row' spacing='6px' alignItems='center' flexWrap='wrap'>
              <Typography fontWeight='700'>{w.name}</Typography>
              {w.code && <Chip size='small' label={w.code} />}
              <Chip
                size='small'
                label={w.is_active ? 'Active' : 'Inactive'}
                color={w.is_active ? 'success' : 'default'}
              />
              <Chip size='small' variant='outlined' label={w.type} />
            </Stack>
            <Typography variant='body2' color='text.secondary' mt='4px'>
              {[w.city, w.region, w.country, w.postal_code].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Box>
          <Stack direction='row' spacing='4px'>
            <Tooltip title='Edit'>
              <IconButton size='small' onClick={() => { setEditing(w); setDlgOpen(true); }}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton size='small' color='error' onClick={() => remove(w)}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        ))}
      </LoadingOverlay>
      <WarehouseDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        brandId={brandId}
        editing={editing}
        onSaved={reload}
      />
    </Box>
  );
}
